import { useState, useEffect } from 'react';

export default function JsonEditor({ value, onChange, placeholder = '{}' }) {
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [parsedJson, setParsedJson] = useState(null);

  useEffect(() => {
    if (typeof value === 'string') {
      setJsonString(value);
      validateJson(value);
    } else {
      const formatted = JSON.stringify(value, null, 2);
      setJsonString(formatted);
      setParsedJson(value);
    }
  }, [value]);

  const validateJson = (str) => {
    if (!str.trim()) {
      setParsedJson({});
      setError('');
      return true;
    }

    try {
      const parsed = JSON.parse(str);
      setParsedJson(parsed);
      setError('');
      return true;
    } catch (e) {
      setParsedJson(null);
      setError(e.message);
      return false;
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setJsonString(newValue);
    
    if (validateJson(newValue)) {
      onChange(newValue);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      onChange(formatted);
      setError('');
    } catch (e) {
      setError('Cannot format invalid JSON');
    }
  };

  const renderJsonPreview = (obj, indent = 0) => {
    if (obj === null) {
      return <span className="text-purple-600">null</span>;
    }
    
    if (typeof obj === 'boolean') {
      return <span className="text-purple-600">{obj.toString()}</span>;
    }
    
    if (typeof obj === 'number') {
      return <span className="text-blue-600">{obj}</span>;
    }
    
    if (typeof obj === 'string') {
      return <span className="text-green-600">"{obj}"</span>;
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span>[]</span>;
      
      return (
        <span>
          [
          <div className="pl-4">
            {obj.map((item, i) => (
              <div key={i}>
                {renderJsonPreview(item, indent + 1)}
                {i < obj.length - 1 && ','}
              </div>
            ))}
          </div>
          ]
        </span>
      );
    }
    
    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) return <span>{'{}'}</span>;
      
      return (
        <span>
          {'{'}
          <div className="pl-4">
            {keys.map((key, i) => (
              <div key={key}>
                <span className="text-red-600">"{key}"</span>
                <span className="text-gray-500">: </span>
                {renderJsonPreview(obj[key], indent + 1)}
                {i < keys.length - 1 && ','}
              </div>
            ))}
          </div>
          {'}'}
        </span>
      );
    }
    
    return <span>{String(obj)}</span>;
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">
          Metadata (JSON)
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={formatJson}
            disabled={!!error}
            className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🔧 Format
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
          >
            {showPreview ? '📝 Edit' : '👁️ Preview'}
          </button>
        </div>
      </div>

      {showPreview ? (
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 font-mono text-sm overflow-auto max-h-64">
          {parsedJson !== null ? (
            <div className="text-left">
              {renderJsonPreview(parsedJson)}
            </div>
          ) : (
            <div className="text-red-600">Invalid JSON</div>
          )}
        </div>
      ) : (
        <textarea
          value={jsonString}
          onChange={handleChange}
          rows={6}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
          }`}
        />
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
          <span className="font-semibold">❌ JSON Error:</span> {error}
        </div>
      )}

      {!error && parsedJson && Object.keys(parsedJson).length > 0 && (
        <div className="text-xs text-green-600 bg-green-50 border border-green-200 rounded p-2">
          ✅ Valid JSON ({Object.keys(parsedJson).length} {Object.keys(parsedJson).length === 1 ? 'field' : 'fields'})
        </div>
      )}
    </div>
  );
}
